import React from "react";
import { Plus, CircleNotch, PencilSimple, Trash, TrashSimple, CaretUp, CaretDown, List, CurrencyCircleDollar, CalendarX, X, Check, Envelope } from "@phosphor-icons/react";
import SalaryStatistics from "./SalaryStatistics";
import EmployeeYearlyStats from "./EmployeeYearlyStats";
import PaymentListTab from "./PaymentListTab";
import ExcludeDaysModal from "./ExcludeDaysModal";
import DpdImportSection from "./DpdImportSection";
import { payoutDateFromRecord } from "../../utils/payoutDate";

const MONTHS = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const ALL_FIELDS = [
  {
    key: "przychod_vat_brutto",
    label: "Przychód VAT Brutto",
    editable: true,
    type: "number",
  },
  {
    key: "doplata_paliwowa",
    label: "Dopłata paliwowa",
    editable: true,
    type: "number",
  },
  {
    key: "przychod",
    label: "Przychód",
    editable: false,
    formula: true,
    type: "number",
  },
  { key: "nota", label: "Nota", editable: true, type: "number" },
  { key: "rata", label: "Rata", editable: true, type: "number", hasNote: true, hidden: true },
  { key: "paliwo", label: "Paliwo (base)", editable: true, type: "number", hidden: true },
  {
    key: "paliwo_total",
    label: "Paliwo razem",
    editable: false,
    hidden: true,
    computed: true,
    type: "number",
  },
  { key: "premia", label: "Premia", editable: true, type: "number" },
  {
    key: "dochod",
    label: "Dochód",
    editable: false,
    formula: true,
    type: "number",
  },
  {
    key: "przelew",
    label: "Przelew",
    editable: false,
    formula: true,
    type: "number",
  },
];

// Расчёт общего paliwo
const getTotalPaliwo = (record) => {
  const base = record.paliwo || 0;
  const items = (record.paliwo_items || []).reduce(
    (sum, v) => sum + (v || 0),
    0,
  );
  return base + items;
};

// Fields shown in table columns. Hidden fields (`hidden: true` in
// ALL_FIELDS) stay in data & calculations but are not rendered as columns
// — e.g. "Rata", whose value is exposed instead as detailed lines in the
// Potrącenia column.
const VISIBLE_FIELDS = ALL_FIELDS.filter((f) => !f.hidden);

// Расчёт przelew с учётом paliwo_items
const getActualPrzelew = (record) => {
  const totalPaliwo = getTotalPaliwo(record);
  
  // Calculate total custom deductions
  const customDeductionsTotal = (record.custom_deductions || []).reduce(
    (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
    0
  );
  
  return (
    (record.dochod || 0) -
    totalPaliwo -
    (record.nota || 0) -
    (parseFloat(record.rata) || 0) -
    customDeductionsTotal
  );
};

export default function SalaryTab({
  records,
  statsRecords,
  employees,
  loading,
  uploadingPDF,
  selectedYear,
  selectedMonth,
  selectedPeriod,
  selectedEmployee,
  onYearChange,
  onMonthChange,
  onPeriodChange,
  onEmployeeChange,
  onPDFUpload,
  onExportPDF,
  onAddSalary,
  onEditSalary,
  onDeleteSalary,
}) {
  const [cleaningUp, setCleaningUp] = React.useState(false);
  const [showAllRecords, setShowAllRecords] = React.useState(false);
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });
  const [showTop5, setShowTop5] = React.useState(false);
  const [subTab, setSubTab] = React.useState('wynagrodzenia'); // Sub-tab switcher
  const [paymentRefreshKey, setPaymentRefreshKey] = React.useState(0);
  // "Wyklucz dni" modal — holds the record being edited or null
  const [excludeDaysRecord, setExcludeDaysRecord] = React.useState(null);
  // Per-row expand toggle for Potrącenia (collapsed sum → "Podrobniej" → detailed list)
  const [expandedPotracenia, setExpandedPotracenia] = React.useState({});

  // Function to cleanup invalid records
  const handleCleanup = async () => {
    if (!window.confirm("Usunąć wszystkie nieprawidłowe wpisy (z błędnymi datami lub stworzone przez stare kredyty)?")) {
      return;
    }

    setCleaningUp(true);
    try {
      const API = `${window.location.origin}/api`;
      const token = localStorage.getItem("access_token");
      
      const response = await fetch(`${API}/salary/cleanup-invalid`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      
      if (result.deleted_count > 0) {
        alert(`✅ Usunięto ${result.deleted_count} nieprawidłowych wpisów!\n\nUsunięte wpisy:\n${result.records.map(r => `- ${r.employee_name}: ${r.period} (rata: ${r.rata} zł)`).join('\n')}`);
        // Reload the page to show updated data
        window.location.reload();
      } else {
        alert("✅ Nie znaleziono nieprawidłowych wpisów do usunięcia.");
      }
    } catch (error) {
      console.error("Cleanup error:", error);
      alert(`❌ Błąd: ${error.message}\n\nUpewnij się, że backend został zaktualizowany (deploy).`);
    } finally {
      setCleaningUp(false);
    }
  };

  // Backfill missing employee_name on placeholder salary records
  // (auto-created from private credits without employee_name — showed as
  // "Nieznany" and were invisible in this tab).
  const [fixingNames, setFixingNames] = React.useState(false);
  const handleFixOrphanNames = async () => {
    if (!window.confirm("Uzupełnić brakujące nazwy pracowników w rekordach wynagrodzeń (naprawa 'Nieznany')?")) {
      return;
    }
    setFixingNames(true);
    try {
      const API = `${window.location.origin}/api`;
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API}/salary/fix-orphan-names`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      const result = await response.json();
      if (result.fixed_count > 0) {
        alert(`✅ Naprawiono ${result.fixed_count} rekordów.\n${(result.fixed || []).map(r => `- ${r.employee_name}`).join('\n')}`);
        window.location.reload();
      } else {
        alert("✅ Brak rekordów do naprawy — wszystko OK.");
      }
    } catch (error) {
      console.error("Fix orphan names error:", error);
      alert(`❌ Błąd: ${error.message}`);
    } finally {
      setFixingNames(false);
    }
  };

  // Fix orphan-names handler (see above)
  const [dedupingRata, setDedupingRata] = React.useState(false);
  const handleDedupeRata = async () => {
    if (!window.confirm("Usunąć zduplikowane raty (ta sama pożyczka zaaplikowana dwa razy w jednym okresie)? Przelew i saldo pożyczki zostaną przeliczone.")) {
      return;
    }
    setDedupingRata(true);
    try {
      const API = `${window.location.origin}/api`;
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API}/salary/dedupe-rata-refs`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errText || response.statusText}`);
      }
      const result = await response.json();
      if (result.fixed_count > 0) {
        const details = (result.fixed || []).map(f =>
          `• ${f.employee} ${f.period}: rata ${f.rata_before.toFixed(2)} → ${f.rata_after.toFixed(2)} zł`
        ).join("\n");
        alert(`✅ Naprawiono ${result.fixed_count} rekordów:\n\n${details}`);
        window.location.reload();
      } else {
        alert("✅ Brak duplikatów rat — wszystko OK.");
      }
    } catch (err) {
      console.error("Dedupe rata error:", err);
      alert(`❌ Błąd: ${err.message}`);
    } finally {
      setDedupingRata(false);
    }
  };

  // Handle sorting
  const handleSort = (fieldKey) => {
    let direction = 'asc';
    if (sortConfig.key === fieldKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: fieldKey, direction });
  };

  // Build employee lookup for fallback names when employee_name is missing.
  // MUST be declared before the early-return below (rules-of-hooks).
  const employeeNameById = React.useMemo(() => {
    const m = {};
    (employees || []).forEach((e) => {
      const id = e._id || e.id;
      if (id) m[String(id)] = e.name || '';
    });
    return m;
  }, [employees]);

  // Early return for loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // NOW safe to process data
  // Filter based on admin preference and selected period
  let validRecords = records.filter((record) => {
    // Only require the record `id` (for the delete button). Records with
    // missing employee_id / employee_name (e.g. broken imports, orphan
    // placeholders from private credits) MUST still be visible so admin
    // can inspect and delete them.
    if (!record || !record.id) {
      return false;
    }

    // If "show all" is enabled, skip period validation
    if (showAllRecords) {
      return true;
    }

    // Records with missing year/month can't be placed on the timeline
    // → hide only when a specific period is selected; otherwise show.
    if (!record.year || !record.month) {
      return !selectedPeriod;
    }

    // Otherwise, filter out invalid periods (but ONLY when a period filter
    // is selected — an orphan record with bad period metadata still needs
    // to be visible in the default view so admin can delete it).
    if (selectedPeriod) {
      if (!record.period_start || !record.period_end) {
        return false;
      }
      const [start, end] = selectedPeriod.split('-');
      // Normalize: "15" stored in DB should match "16-30" filter
      const normalizedStart = record.period_start === "15" ? "16" : record.period_start;
      const normalizedEnd = record.period_end;

      // Match both start and end
      if (normalizedStart !== start || normalizedEnd !== end) {
        return false;
      }
    }

    return true;
  });

  // Apply Top 5 filter if enabled
  if (showTop5) {
    // Sort by PRZYCHOD (not przelew)
    const recordsWithPrzychod = validRecords.map(r => ({
      ...r,
      calculatedPrzychod: r.przychod || 0
    }));

    validRecords = recordsWithPrzychod
      .sort((a, b) => b.calculatedPrzychod - a.calculatedPrzychod)
      .slice(0, 5);
  }

  // Группировка по сотруднику, затем по месяцу для Podsumowanie
  // Записи без employee_id идут в специальный bucket "__nieznany__",
  // чтобы админ мог их увидеть и удалить (иначе они висят только
  // во вкладке "Do wypłaty" без кнопки Trash).
  const groupedByEmployee =
    validRecords.length > 0
      ? validRecords.reduce((acc, record) => {
          const empId = record.employee_id || '__nieznany__';
          const displayName =
            record.employee_name ||
            (record.employee_id ? employeeNameById[String(record.employee_id)] : '') ||
            'Nieznany (usuń i wgraj ponownie)';
          if (!acc[empId])
            acc[empId] = {
              name: displayName,
              records: [],
              byMonth: {},
            };
          acc[empId].records.push(record);

          // Группировка по месяцу для подсчёта Podsumowanie
          const monthKey = `${record.year}-${record.month}`;
          if (!acc[empId].byMonth[monthKey]) acc[empId].byMonth[monthKey] = [];
          acc[empId].byMonth[monthKey].push(record);

          return acc;
        }, {})
      : {};

  // Функция для расчёта Podsumowanie
  const calculatePodsumowanie = (monthRecords) => {
    if (monthRecords.length < 2) return null;
    const has01_15 = monthRecords.some((r) => r.period_start === "01");
    // Support both "15" (old) and "16" (new)
    const has16_30 = monthRecords.some((r) => r.period_start === "16" || r.period_start === "15");
    if (!has01_15 || !has16_30) return null;

    return {
      przychod_vat_brutto: monthRecords.reduce(
        (sum, r) => sum + (r.przychod_vat_brutto || 0),
        0,
      ),
      doplata_paliwowa: monthRecords.reduce(
        (sum, r) => sum + (r.doplata_paliwowa || 0),
        0,
      ),
      przychod: monthRecords.reduce((sum, r) => sum + (r.przychod || 0), 0),
      nota: monthRecords.reduce((sum, r) => sum + (r.nota || 0), 0),
      rata: monthRecords.reduce((sum, r) => sum + (parseFloat(r.rata) || 0), 0),
      paliwo: monthRecords.reduce((sum, r) => sum + (r.paliwo || 0), 0),
      paliwo_total: monthRecords.reduce((sum, r) => sum + getTotalPaliwo(r), 0),
      premia: monthRecords.reduce((sum, r) => sum + (r.premia || 0), 0),
      dochod: monthRecords.reduce((sum, r) => sum + (r.dochod || 0), 0),
      przelew: monthRecords.reduce((sum, r) => sum + getActualPrzelew(r), 0),
    };
  };

  // Расчет общих сумм по периодам (okres) для всех сотрудников
  const calculatePeriodTotals = () => {
    const periodTotals = {};

    validRecords.forEach((record) => {
      // Additional safety check
      if (
        !record.year ||
        !record.month ||
        !record.period_start ||
        !record.period_end
      )
        return;

      // Normalize old period format: "15" -> "16"
      const normalizedPeriodStart = record.period_start === "15" ? "16" : record.period_start;
      
      const periodKey = `${record.year}-${String(record.month).padStart(2, "0")}-${normalizedPeriodStart}-${record.period_end}`;
      const periodLabel = `${normalizedPeriodStart}-${record.period_end}.${String(record.month).padStart(2, "0")}.${record.year}`;

      if (!periodTotals[periodKey]) {
        periodTotals[periodKey] = {
          label: periodLabel,
          year: record.year,
          month: record.month,
          period_start: normalizedPeriodStart,
          period_end: record.period_end,
          przelew: 0,
          count: 0,
        };
      }

      periodTotals[periodKey].przelew += getActualPrzelew(record);
      periodTotals[periodKey].count += 1;
    });

    return Object.values(periodTotals).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      if (a.month !== b.month) return a.month - b.month;
      // Null-safe sorting for period_start
      const aStart = a.period_start || '';
      const bStart = b.period_start || '';
      return aStart.localeCompare(bStart);
    });
  };

  const periodTotals = calculatePeriodTotals();

  return (
    <div>
      {/* Sub-tab Switcher */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setSubTab('wynagrodzenia')}
          className={`py-3 px-3 sm:px-6 whitespace-nowrap shrink-0 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            subTab === 'wynagrodzenia'
              ? 'border-blue-600 text-foreground'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <List className="w-5 h-5 inline mr-2" />
          Wynagrodzenia
        </button>
        <button
          onClick={() => {
            setSubTab('do-wyplaty');
            setPaymentRefreshKey((k) => k + 1);
          }}
          className={`py-3 px-3 sm:px-6 whitespace-nowrap shrink-0 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            subTab === 'do-wyplaty'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <CurrencyCircleDollar className="w-5 h-5 inline mr-2" />
          Do wypłaty
        </button>
        <button
          onClick={() => setSubTab('import-dpd')}
          data-testid="salary-subtab-import-dpd"
          className={`py-3 px-3 sm:px-6 whitespace-nowrap shrink-0 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            subTab === 'import-dpd'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Envelope className="w-5 h-5 inline mr-2" />
          Import DPD
        </button>
      </div>

      {/* Render appropriate tab */}
      {subTab === 'do-wyplaty' ? (
        <PaymentListTab key={paymentRefreshKey} />
      ) : subTab === 'import-dpd' ? (
        <DpdImportSection />
      ) : (
        <>
      {/* Filters Section - Two rows layout */}
      <div className="mb-6 space-y-3">
        {/* First Row: Year, Month, Employee, Quick Filters */}
        <div className="flex flex-wrap items-stretch gap-3">
          {/* Year */}
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm font-semibold text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px]"
          >
            {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Month */}
          <select
            value={selectedMonth || ""}
            onChange={(e) => onMonthChange(e.target.value ? Number(e.target.value) : null)}
            className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm font-semibold text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
          >
            <option value="">Wszystkie miesiące</option>
            {MONTHS.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>

          {/* Employee */}
          <select
            value={selectedEmployee || ""}
            onChange={(e) => onEmployeeChange(e.target.value || null)}
            className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm font-semibold text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px] flex-1"
          >
            <option value="">Wszyscy pracownicy</option>
            {employees.map((emp) => (
              <option key={emp._id} value={emp._id}>{emp.name}</option>
            ))}
          </select>

          {/* Period Filter */}
          <select
            value={selectedPeriod || ""}
            onChange={(e) => onPeriodChange(e.target.value || null)}
            className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm font-semibold text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]"
          >
            <option value="">Wszystkie okresy</option>
            <option value="01-15">01-15</option>
            <option value="16-30">16-30</option>
          </select>

          {/* Quick Filters Button */}
          <button className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm font-semibold text-base hover:bg-gray-50 transition-colors flex items-center gap-2">
            <span>🔍</span>
            <span className="hidden sm:inline">Szybko filtry</span>
          </button>
        </div>

        {/* Second Row: Top 5 checkbox (left) and Action Buttons (right) */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Top 5 Checkbox */}
          <label className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={showTop5}
              onChange={(e) => setShowTop5(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-foreground focus:ring-blue-500"
            />
            <span className="font-medium text-base">Top 5</span>
          </label>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Export PDF - Light Blue */}
            <button 
              onClick={onExportPDF}
              className="px-5 py-3 bg-blue-400 hover:bg-blue-500 text-white rounded-lg font-semibold text-base shadow-sm transition-colors flex items-center gap-2"
              data-testid="export-pdf-btn"
            >
              <span>📄</span>
              <span className="hidden sm:inline">Eksportuj do PDF</span>
            </button>

            {/* Upload PDF - Light gray (file picker) */}
            <label
              className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-lg font-semibold text-base shadow-sm cursor-pointer transition-colors flex items-center gap-2"
              data-testid="upload-pdf-btn"
            >
              <input type="file" accept=".pdf" multiple onChange={onPDFUpload} className="hidden" disabled={uploadingPDF} />
              {uploadingPDF ? (
                <>
                  <CircleNotch className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">Przetwarzanie...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Wgraj PDF</span>
                </>
              )}
            </label>

            {/* Fix Orphan Names - amber (backfill missing employee_name on placeholders) */}
            <button
              onClick={handleFixOrphanNames}
              disabled={fixingNames}
              className="px-4 py-3 bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 rounded-lg font-semibold text-sm shadow-sm transition-colors flex items-center gap-2 disabled:opacity-60"
              title="Napraw rekordy z pustym imieniem pracownika ('Nieznany')"
              data-testid="fix-orphan-names-btn"
            >
              {fixingNames ? (
                <CircleNotch className="w-4 h-4 animate-spin" />
              ) : (
                <span>🔧</span>
              )}
              <span className="hidden sm:inline">Napraw „Nieznany”</span>
            </button>

            {/* Dedupe rata refs — one-time cleanup for records with 2 rats
                of the same credit accidentally applied to one period. */}
            <button
              onClick={handleDedupeRata}
              disabled={dedupingRata}
              className="px-4 py-3 bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300 rounded-lg font-semibold text-sm shadow-sm transition-colors flex items-center gap-2 disabled:opacity-60"
              title="Usuń zduplikowane raty (ta sama pożyczka 2× w jednym okresie)"
              data-testid="dedupe-rata-btn"
            >
              {dedupingRata ? (
                <CircleNotch className="w-4 h-4 animate-spin" />
              ) : (
                <span>🩹</span>
              )}
              <span className="hidden sm:inline">Napraw dubel rat</span>
            </button>

            {/* Add Entry MANUALLY - Dark Blue button (opens modal) */}
            <button
              onClick={onAddSalary}
              className="px-5 py-3 bg-foreground hover:bg-black text-white rounded-lg font-semibold text-base shadow-sm transition-colors flex items-center gap-2"
              data-testid="add-salary-btn"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Dodaj wpis</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards - always show even if empty */}
      {!loading && (
        <SalaryStatistics 
          records={statsRecords && statsRecords.length > 0 ? statsRecords : validRecords}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
        />
      )}

      {/* Общая сумма выплат по периодам — moved into unified summary card */}

      {validRecords.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          Brak poprawnych danych dla wybranych filtrów.
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl shadow-md overflow-x-auto" data-testid="salary-unified-table">
          <table className="w-full">
            <thead className="bg-muted sticky top-0 z-10">
              <tr>
                <th className="text-left py-2 px-3 field-label uppercase text-xs tracking-wide text-slate-600 font-bold">
                  Okres
                </th>
                {VISIBLE_FIELDS.map((f) => (
                  <th
                    key={f.key}
                    onClick={() => handleSort(f.key)}
                    className="text-right py-2 px-3 field-label uppercase text-xs tracking-wide text-slate-600 font-bold whitespace-nowrap cursor-pointer hover:bg-slate-200/50 transition-colors"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>{f.label}</span>
                      {sortConfig.key === f.key && (
                        sortConfig.direction === 'asc' ? <CaretUp className="w-4 h-4" /> : <CaretDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="text-left py-3 px-4 field-label uppercase text-xs tracking-wider text-slate-600 font-bold whitespace-nowrap">
                  Potrącenia
                </th>
                <th className="text-right py-3 px-4 field-label uppercase text-xs tracking-wider text-slate-600 font-bold">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedByEmployee).map(
                ([empId, { name, records: empRecords, byMonth }]) => (
                  <React.Fragment key={empId}>
                    {/* Employee section banner — contains yearly stats card */}
                    <tr
                      className="bg-transparent"
                      data-testid={`employee-section-${empId}`}
                    >
                      <td colSpan={VISIBLE_FIELDS.length + 3} className="p-0 border-0">
                        <div className="pt-6 pb-2">
                          <EmployeeYearlyStats
                            employeeName={name}
                            records={empRecords}
                            selectedYear={selectedYear}
                            selectedMonth={selectedMonth}
                          />
                        </div>
                      </td>
                    </tr>
                    {Object.entries(byMonth)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([monthKey, monthRecords]) => {
                          const [year, month] = monthKey.split("-");
                          const podsumowanie =
                            calculatePodsumowanie(monthRecords);
                          
                          // Apply sorting
                          const sortedRecords = [...monthRecords].sort((a, b) => {
                            // First sort by period_start (null-safe)
                            const aStart = a.period_start || '';
                            const bStart = b.period_start || '';
                            const periodCompare = aStart.localeCompare(bStart);
                            
                            // Then apply column sorting if set
                            if (sortConfig.key) {
                              const aVal = a[sortConfig.key] || 0;
                              const bVal = b[sortConfig.key] || 0;
                              
                              if (typeof aVal === 'number' && typeof bVal === 'number') {
                                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                              }
                            }
                            
                            return periodCompare;
                          });

                          return (
                            <React.Fragment key={monthKey}>
                              {sortedRecords.map((record, idx) => {
                                const paliwoTotal = getTotalPaliwo(record);
                                const actualPrzelew = getActualPrzelew(record);
                                const hasPaliwoItems =
                                  (record.paliwo_items || []).length > 0;

                                return (
                                  <tr
                                    key={record.id}
                                    className={`table-row hover:bg-muted transition-colors duration-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                                    data-testid={`salary-row-${record.id}`}
                                  >
                                    <td className="py-2.5 px-3 border-b border-gray-100">
                                      <span className={`period-badge px-2.5 py-1 rounded-full text-xs font-semibold ${!record.period_start || !record.period_end ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-muted text-foreground border border-border'}`}>
                                        {record.period_start === "15" ? "16" : (record.period_start || "??")}-
                                        {record.period_end || "??"}.
                                        {String(record.month).padStart(2, "0")}.
                                        {record.year}
                                      </span>
                                    </td>
                                    {VISIBLE_FIELDS.map((f) => {
                                      let value;
                                      if (f.key === "paliwo_total") {
                                        value = paliwoTotal;
                                      } else if (f.key === "przelew") {
                                        value = actualPrzelew;
                                      } else {
                                        value = record[f.key];
                                      }

                                      const isHighlighted =
                                        f.key === "paliwo_total" &&
                                        hasPaliwoItems;
                                      const showRataInfo =
                                        f.key === "rata" && (record.rata_info || record.rata_note);
                                      const payoutDate =
                                        f.key === "przelew"
                                          ? payoutDateFromRecord(record)
                                          : "";

                                      return (
                                        <td
                                          key={f.key}
                                          className={`py-2.5 px-3 text-right font-semibold text-sm border-b border-gray-100 ${isHighlighted ? "text-emerald-600" : "text-slate-700"} ${f.key === "przelew" ? "text-base font-bold" : ""}`}
                                        >
                                          {typeof value === "number"
                                            ? value.toLocaleString("pl-PL", {
                                                minimumFractionDigits: 2,
                                              })
                                            : value || "0,00"}
                                          {payoutDate && (
                                            <div
                                              className="text-[10px] font-normal text-slate-500 mt-0.5 whitespace-nowrap"
                                              title="Faktyczna wypłata (2 tygodnie po zakończeniu okresu)"
                                              data-testid={`payout-date-${record.id}`}
                                            >
                                              wypłata: {payoutDate}
                                            </div>
                                          )}
                                          {showRataInfo && (
                                            <span className="ml-1 text-xs text-foreground font-normal">
                                              ({record.rata_info || record.rata_note})
                                            </span>
                                          )}
                                          {isHighlighted && (
                                            <span
                                              className="ml-1 text-xs font-normal cursor-help"
                                              title={
                                                "Zaprawki:\n" +
                                                (record.paliwo_items || [])
                                                  .map(
                                                    (v, i) =>
                                                      `  #${i + 1}: ${Number(
                                                        v || 0
                                                      ).toFixed(2)} zł`
                                                  )
                                                  .join("\n") +
                                                ((record.paliwo || 0) > 0
                                                  ? `\n  baza: ${Number(
                                                      record.paliwo || 0
                                                    ).toFixed(2)} zł`
                                                  : "")
                                              }
                                              data-testid={`paliwo-breakdown-${record.id}`}
                                            >
                                              (+
                                              {
                                                (record.paliwo_items || [])
                                                  .length
                                              }
                                              )
                                            </span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    
                                    {/* Potrącenia — łączony widok: paliwo, raty, pożyczki, dodatkowe */}
                                    <td className="py-3 px-4 text-right font-medium align-top">
                                      {(() => {
                                        const lines = [];
                                        // Paliwo items (each is one draft applied to this salary)
                                        (record.paliwo_items || []).forEach((amt, i) => {
                                          const details = (record.paliwo_details || [])[i];
                                          const dateStr = details?.data || details?.date || '';
                                          const label = dateStr
                                            ? `Paliwo — tankowanie z dnia ${dateStr}`
                                            : `Paliwo #${i + 1}`;
                                          lines.push({ label, amount: Number(amt) || 0, color: 'text-orange-700' });
                                        });
                                        // Base paliwo (legacy amount without breakdown)
                                        if ((record.paliwo || 0) > 0 && !(record.paliwo_items || []).length) {
                                          lines.push({ label: 'Paliwo', amount: Number(record.paliwo), color: 'text-orange-700' });
                                        }
                                        // Rata — prefer detailed per-credit
                                        // rows (from applied_rata_refs) so
                                        // the user sees "Rata Audi #8 — 20.09.2026"
                                        // instead of just "Rata: 1445".
                                        const rataDetails = record.rata_details || [];
                                        if (rataDetails.length > 0) {
                                          const detailsSum = rataDetails.reduce(
                                            (s, d) => s + (Number(d.amount) || 0), 0
                                          );
                                          rataDetails.forEach((d) => {
                                            const dueSuffix = d.due_date ? ` — ${d.due_date}` : '';
                                            const numSuffix = d.payment_number != null && d.total_payments
                                              ? ` (${d.payment_number}/${d.total_payments})`
                                              : '';
                                            lines.push({
                                              label: `Rata ${d.credit_name}${numSuffix}${dueSuffix}`,
                                              amount: Number(d.amount) || 0,
                                              color: 'text-rose-700',
                                            });
                                          });
                                          // Show residual only if aggregated rata is bigger than sum of details
                                          const residual = (Number(record.rata) || 0) - detailsSum;
                                          if (residual > 0.01) {
                                            lines.push({
                                              label: record.rata_info || record.rata_note || 'Rata (pozostała)',
                                              amount: residual,
                                              color: 'text-rose-700',
                                            });
                                          }
                                        } else if ((record.rata || 0) > 0) {
                                          const rataLabel = record.rata_info || record.rata_note || 'Rata';
                                          lines.push({ label: rataLabel, amount: Number(record.rata), color: 'text-rose-700' });
                                        }
                                        // Custom deductions (pożyczka, usługi sieciowe, itp.)
                                        // Skip entries that duplicate the rata rows: legacy edit-modal
                                        // migration used to persist rata into custom_deductions as
                                        // "Rata: 8/24" or "Rata Audi #8" — with rata_details / rata
                                        // now shown separately, those become duplicates.
                                        (record.custom_deductions || []).forEach((d) => {
                                          const rawName = (d?.name || '').trim();
                                          const src = (d?.source || '').toLowerCase();
                                          const isRataDup =
                                            src === 'rata_kredyt' ||
                                            /^rata\b/i.test(rawName);
                                          if (isRataDup) return;
                                          lines.push({
                                            label: rawName || 'Potrącenie',
                                            amount: parseFloat(d.amount) || 0,
                                            color: 'text-slate-700',
                                          });
                                        });
                                        if (lines.length === 0) {
                                          return <span className="text-muted-foreground">—</span>;
                                        }
                                        const totalDeductions = lines.reduce(
                                          (s, l) => s + (Number(l.amount) || 0), 0
                                        );
                                        const expanded = !!expandedPotracenia[record.id];
                                        return (
                                          <div className="space-y-1 text-xs">
                                            <button
                                              type="button"
                                              onClick={() => setExpandedPotracenia((prev) => ({ ...prev, [record.id]: !prev[record.id] }))}
                                              className="w-full flex items-center justify-end gap-1.5 hover:bg-slate-50 rounded-sm px-1 py-0.5 group"
                                              data-testid={`potracenia-toggle-${record.id}`}
                                              title={expanded ? "Ukryj szczegóły" : "Pokaż szczegóły potrąceń"}
                                            >
                                              <span className="text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-slate-700">
                                                {expanded ? "Ukryj" : `Szczegóły (${lines.length})`}
                                              </span>
                                              <span className="font-mono font-bold tabular-nums text-orange-700">
                                                {totalDeductions.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                              </span>
                                              {expanded ? <CaretUp className="w-3 h-3 text-slate-500" /> : <CaretDown className="w-3 h-3 text-slate-500" />}
                                            </button>
                                            {expanded && (
                                              <div className="space-y-0.5 pt-1 border-t border-slate-100">
                                                {lines.map((l, i) => (
                                                  <div key={i} className={`${l.color} flex items-center justify-end gap-1.5`}>
                                                    <span className="text-muted-foreground truncate max-w-[220px]" title={l.label}>{l.label}:</span>
                                                    <span className="font-mono font-semibold tabular-nums">
                                                      {l.amount.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    
                                    <td className="py-3 px-4 text-right">
                                      {(record.daily_breakdown && Object.keys(record.daily_breakdown).length > 0) && (
                                        <button
                                          onClick={() => setExcludeDaysRecord(record)}
                                          className={`p-2 rounded-sm ${(record.excluded_days || []).length > 0 ? "text-amber-700 bg-amber-100 hover:bg-amber-200" : "hover:bg-muted text-slate-600"}`}
                                          title={(record.excluded_days || []).length > 0
                                            ? `Wykluczono ${record.excluded_days.length} dni · kliknij aby zmienić`
                                            : "Wyklucz dni (np. gdy jeździłeś zamiast pracownika)"}
                                          data-testid={`exclude-days-${record.id}`}
                                        >
                                          <CalendarX className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => onEditSalary(record)}
                                        className="p-2 hover:bg-muted rounded-sm"
                                        data-testid={`edit-salary-${record.id}`}
                                      >
                                        <PencilSimple className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          onDeleteSalary(record.id)
                                        }
                                        className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                                        data-testid={`delete-salary-${record.id}`}
                                      >
                                        <Trash className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              {podsumowanie && (
                                <tr
                                  className="bg-muted font-bold border-t-4 border-emerald-400 hover:from-emerald-100 hover:via-green-100 hover:to-teal-100 transition-colors duration-300"
                                  data-testid={`podsumowanie-${monthKey}`}
                                >
                                  <td className="py-3 px-3 border-b border-emerald-200">
                                    <div className="flex items-center gap-2">
                                      <div className="bg-emerald-500 p-1 rounded-md">
                                        <span className="text-white text-sm">✓</span>
                                      </div>
                                      <span className="text-emerald-700 uppercase text-sm font-extrabold tracking-wide">
                                        Podsumowanie {MONTHS[parseInt(month) - 1]}
                                      </span>
                                    </div>
                                  </td>
                                  {VISIBLE_FIELDS.map((f) => (
                                    <td
                                      key={f.key}
                                      className="py-3 px-3 text-right text-emerald-700 text-sm border-b border-emerald-200"
                                    >
                                      {podsumowanie[f.key]?.toLocaleString(
                                        "pl-PL",
                                        { minimumFractionDigits: 2 },
                                      )} {f.key === "przelew" ? "zł" : ""}
                                    </td>
                                  ))}
                                  <td className="py-5 px-6 border-b border-emerald-200"></td>
                                  <td className="py-5 px-6 border-b border-emerald-200"></td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                  </React.Fragment>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {excludeDaysRecord && (
        <ExcludeDaysModal
          record={excludeDaysRecord}
          onClose={() => setExcludeDaysRecord(null)}
          onSaved={() => {
            // Signal parent to refresh the salary list — reuse existing flow.
            setExcludeDaysRecord(null);
            if (typeof onEditSalary === "function") {
              // Trigger a light refresh — reload page is heavy-handed but simple
              window.location.reload();
            }
          }}
        />
      )}
    </div>
  );
}
